// src/Context/SubscriptionContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState({
    isSubscribed: false,
    invoiceCount: 0,
    subscriptionEndDate: null,
    isExpired: false,
    hasEverSubscribed: false, // NEW: Track if user ever had a subscription
    loading: true
  });

  const FREE_PLAN_LIMIT = 2;

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
      
      // Check expiry every hour
      const interval = setInterval(fetchSubscriptionData, 60 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      setSubscriptionData({ 
        isSubscribed: false, 
        invoiceCount: 0, 
        subscriptionEndDate: null,
        isExpired: false,
        hasEverSubscribed: false,
        loading: false 
      });
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    try {
      // Get user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('subscribed, inv_count')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      // Get subscription data - check if user ever had a subscription
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('active, end_date, start_date')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false })
        .limit(1)
        .single();

      // Determine if user has ever subscribed
      const hasEverSubscribed = subData !== null;

      // Check if subscription is expired
      let isActive = false;
      let isExpired = false;
      let endDate = null;

      if (subData && subData.end_date) {
        endDate = subData.end_date;
        const subscriptionEndDate = new Date(subData.end_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        isActive = subData.active && subscriptionEndDate >= today;
        isExpired = subscriptionEndDate < today;

        // If expired, automatically update the subscription status
        if (isExpired && subData.active) {
          await handleExpiredSubscription(user.id);
        }
      }

      setSubscriptionData({
        isSubscribed: userData.subscribed && isActive,
        invoiceCount: userData.inv_count || 0,
        subscriptionEndDate: endDate,
        isExpired: isExpired,
        hasEverSubscribed: hasEverSubscribed,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setSubscriptionData({ 
        isSubscribed: false, 
        invoiceCount: 0, 
        subscriptionEndDate: null,
        isExpired: false,
        hasEverSubscribed: false,
        loading: false 
      });
    }
  };

  const handleExpiredSubscription = async (userId) => {
    try {
      // Update user to not subscribed
      const { error: userError } = await supabase
        .from('users')
        .update({ subscribed: false })
        .eq('id', userId);

      if (userError) throw userError;

      // Mark subscription as inactive
      const { error: subError } = await supabase
        .from('subscriptions')
        .update({ active: false })
        .eq('user_id', userId)
        .eq('active', true);

      if (subError) throw subError;

      console.log('Expired subscription handled');
    } catch (error) {
      console.error('Error handling expired subscription:', error);
    }
  };

  const incrementInvoiceCount = async () => {
    try {
      const newCount = subscriptionData.invoiceCount + 1;
      
      const { error } = await supabase
        .from('users')
        .update({ inv_count: newCount })
        .eq('id', user.id);

      if (error) throw error;

      setSubscriptionData(prev => ({
        ...prev,
        invoiceCount: newCount
      }));

      return { success: true, newCount };
    } catch (error) {
      console.error('Error incrementing invoice count:', error);
      return { success: false, error };
    }
  };

  const canCreateInvoice = () => {
    // If currently subscribed and not expired, unlimited access
    if (subscriptionData.isSubscribed && !subscriptionData.isExpired) {
      return true;
    }

    // If subscription expired OR user has ever subscribed
    // Lock everything - no free invoices
    if (subscriptionData.isExpired || subscriptionData.hasEverSubscribed) {
      return false;
    }

    // If never subscribed (pure free user), allow up to FREE_PLAN_LIMIT
    return subscriptionData.invoiceCount < FREE_PLAN_LIMIT;
  };

  const getRemainingInvoices = () => {
    // If currently subscribed and not expired
    if (subscriptionData.isSubscribed && !subscriptionData.isExpired) {
      return Infinity;
    }

    // If expired or has ever subscribed, no free invoices available
    if (subscriptionData.isExpired || subscriptionData.hasEverSubscribed) {
      return 0;
    }

    // Pure free user
    return Math.max(0, FREE_PLAN_LIMIT - subscriptionData.invoiceCount);
  };

  // src/Context/SubscriptionContext.js
// Replace the hasFeatureAccess function with this:

const hasFeatureAccess = (feature) => {
  // If currently subscribed and not expired - full access
  if (subscriptionData.isSubscribed && !subscriptionData.isExpired) {
    return true;
  }

  // If expired or has ever subscribed - LOCK EVERYTHING except dashboard and profile
  if (subscriptionData.isExpired || subscriptionData.hasEverSubscribed) {
    return false; // Lock all features
  }

  // Pure free user (never subscribed) - only basic features
  const freeFeatures = ['invoice-generation', 'invoice-tracking'];
  return freeFeatures.includes(feature);
};

  const getDaysUntilExpiry = () => {
    if (!subscriptionData.subscriptionEndDate) return null;
    
    const endDate = new Date(subscriptionData.subscriptionEndDate);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getSubscriptionStatus = () => {
    if (subscriptionData.isSubscribed && !subscriptionData.isExpired) {
      return 'active';
    }
    if (subscriptionData.isExpired) {
      return 'expired';
    }
    if (subscriptionData.hasEverSubscribed) {
      return 'inactive'; // Had subscription before but not active
    }
    return 'free'; // Never subscribed
  };

  const value = {
    ...subscriptionData,
    FREE_PLAN_LIMIT,
    canCreateInvoice,
    getRemainingInvoices,
    hasFeatureAccess,
    incrementInvoiceCount,
    refreshSubscription: fetchSubscriptionData,
    getDaysUntilExpiry,
    getSubscriptionStatus
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};